import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { I2gNgTrackingModule } from 'i2g-ng-tracking';
import { I2gNgUiModule } from 'i2g-ng-ui';
import { DateTimeService } from 'i2g-ng-utils';
import { OverdueInvoicesExpandedDialogComponent } from '../home/components';
import { ToasterModule } from '../toaster/toaster.module';
import { ImageBuilder } from './builders/image/image.builder';
import { AttachmentErrorDialogComponent } from './components/attachment-error-dialog/attachment-error-dialog.component';
import { ChangeEmailDialogComponent } from './components/change-email-dialog/change-email-dialog.component';
import { CheckmarkListComponent } from './components/checkmark-list/checkmark-list.component';
import { CheckmarkComponent } from './components/checkmark/checkmark.component';
import { CollapsibleSectionComponent } from './components/collapsible-section/collapsible-section.component';
import { CopyToClipboardComponent } from './components/copy-to-clipboard/copy-to-clipboard.component';
import { CreateDropdownComponent } from './components/create-dropdown/create-dropdown.component';
import { CreateJobDialogComponent } from './components/create-job-dialog/create-job-dialog.component';
import { DocumentLinkComponent } from './components/document-link/document-link.component';
import { DocumentStatusComponent } from './components/document-status/document-status.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { GlobalBannerComponent } from './components/global-banner/global-banner.component';
import { GlobalBannerService } from './components/global-banner/global-banner.service';
import {
  GlobalHeaderComponent,
  GlobalHeaderComponentDirectives,
} from './components/global-header/global-header.component';
import { GoBackButtonComponent } from './components/go-back-button/go-back-button.component';
import { HorizontalNavComponent } from './components/horizontal-nav/horizontal-nav.component';
import { IconUrlComponent } from './components/icon-url/icon-url.component';
import { IconComponent } from './components/icon/icon.component';
import { IframeComponent } from './components/iframe/iframe.component';
import { LabeledSectionComponent } from './components/labeled-section/labeled-section.component';
import { LinkComponent } from './components/link/link.component';
import { MessageToastComponent } from './components/message-toast/message-toast.component';
import { OverviewMetricListComponent } from './components/overview-metric-list/overview-metric-list.component';
import { OverviewMetricComponent } from './components/overview-metric/overview-metric.component';
import { I2gMoneyOnboardingDialogComponent } from './components/payments/i2g-money-onboarding-dialog/i2g-money-onboarding-dialog.component';
import { PayPalOnboardingDialogComponent } from './components/payments/paypal-onboarding-dialog/paypal-onboarding-dialog.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { RouterLinkComponent } from './components/router-link/router-link.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { AutoCompleteDirective } from './directives/auto-complete/auto-complete.directive';
import { DatePickerDirective } from './directives/date-picker/date-picker.directive';
import { EmailTagDirective } from './directives/email-tag/email-tag.directive';
import { EscHandlerDirective } from './directives/esc-handler/esc-handler.directive';
import { FillToBottomDirective } from './directives/fill-to-bottom/fill-to-bottom.directive';
import { FitTextDirective } from './directives/fit-text/fit-text.directive';
import { FocusableDirective } from './directives/focusable/focusable.directive';
import { FormatAsNumberDirective } from './directives/format-as-number/format-as-number.directive';
import { HorizontalNavDirective } from './directives/horizontal-nav/horizontal-nav.directive';
import { MultiLineTruncateDirective } from './directives/multi-line-truncate/multi-line-truncate.directive';
import { OverflowShadowDirective } from './directives/overflow-shadow/overflow-shadow.directive';
import { PreventWindowUnloadingDirective } from './directives/prevent-window-unloading/prevent-window-unloading.directive';
import { StickyDirective } from './directives/sticky/sticky.directive';
import { TrackableDirective } from './directives/trackable/trackable.directive';
import { WaypointsDirective } from './directives/waypoints/waypoints.directive';
import { CanDeactivateGuard } from './guards/can-deactivate.guard';
import { LocalDevGuard } from './guards/local-dev.guard';
import { AmPmTimePipe } from './pipes/am-pm-time/am-pm-time.pipe';
import { CapitalizePipe } from './pipes/capitalize/capitalize.pipe';
import { EmailLinkPipe } from './pipes/email-link/email-link.pipe';
import { FillPipe } from './pipes/fill/fill.pipe';
import { FriendlyDatePipe } from './pipes/friendly-date/friendly-date.pipe';
import { FriendlyDurationPipe } from './pipes/friendly-duration/friendly-duration.pipe';
import { KebabCasePipe } from './pipes/kebab-case/kebab-case.pipe';
import { KeyValuePairPipe } from './pipes/key-value-pair/key-value-pair.pipe';
import { LinkifyPipe } from './pipes/linkify/linkify.pipe';
import { MoneyPipe } from './pipes/money/money.pipe';
import { PercentagePipe } from './pipes/percentage/percentage.pipe';
import { SafePipe } from './pipes/safe/safe.pipe';
import { SnakeCasePipe } from './pipes/snake-case/snake-case.pipe';
import { CalculatorService } from './services/calculator/calculator.service';
import { EmailService } from './services/email/email.service';
import { ImageService } from './services/image/image.service';
import { ItemSaveService } from './services/item-save/item-save.service';
import { LoggerService } from './services/logger/logger.service';
import { LoggerStore } from './services/logger/logger.store';
import { MoneyService } from './services/money/money.service';
import { UrlService } from './services/url/url.service';
import { UtilsService } from './services/utils/utils.service';
// eslint-disable-next-line max-len
import { PayPalTransactionsHistoryDialogComponent } from '../settings/components/paypal-transactions-history-dialog/paypal-transactions-history-dialog.component';
import { PayPalTransactionsComponent } from '../settings/components/paypal-transactions/paypal-transactions.component';
import { PaypalUnlinkDialogComponent } from '../settings/components/paypal-unlink-dialog/paypal-unlink-dialog.component';

/*
  NOTE: dialog components should be put in shared module because it needs to be specified in the entryComponents.
  EntryComponents requires the module to be declared in `app.module`.
  If we put the dialog component inside a feature module, we should declare
  the feature module into `app.module` which is a bit odd
*/
const dialogs = [
  ChangeEmailDialogComponent,
  OverdueInvoicesExpandedDialogComponent,
  CreateJobDialogComponent,
  AttachmentErrorDialogComponent,
  I2gMoneyOnboardingDialogComponent,
  PayPalOnboardingDialogComponent,
  PayPalTransactionsHistoryDialogComponent,
  PaypalUnlinkDialogComponent,
];

const components = [
  IconComponent,
  IconUrlComponent,
  DropdownComponent,
  LinkComponent,
  CollapsibleSectionComponent,
  MessageToastComponent,
  RouterLinkComponent,
  GlobalBannerComponent,
  TooltipComponent,
  GlobalHeaderComponent,
  LabeledSectionComponent,
  ProgressBarComponent,
  CheckmarkComponent,
  CheckmarkListComponent,
  DocumentStatusComponent,
  CreateDropdownComponent,
  OverviewMetricComponent,
  OverviewMetricListComponent,
  HorizontalNavComponent,
  DocumentLinkComponent,
  CopyToClipboardComponent,
  GoBackButtonComponent,
  IframeComponent,
  PayPalTransactionsComponent,
  ...dialogs,
];
const directives = [
  EscHandlerDirective,
  AutoCompleteDirective,
  FocusableDirective,
  PreventWindowUnloadingDirective,
  DatePickerDirective,
  MultiLineTruncateDirective,
  FormatAsNumberDirective,
  TrackableDirective,
  EmailTagDirective,
  WaypointsDirective,
  FillToBottomDirective,
  FitTextDirective,
  HorizontalNavDirective,
  StickyDirective,
  OverflowShadowDirective,
  ...GlobalHeaderComponentDirectives,
];
const pipes = [
  MoneyPipe,
  FriendlyDatePipe,
  FriendlyDurationPipe,
  AmPmTimePipe,
  CapitalizePipe,
  KeyValuePairPipe,
  SnakeCasePipe,
  KebabCasePipe,
  SafePipe,
  FillPipe,
  PercentagePipe,
  LinkifyPipe,
  EmailLinkPipe,
];

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ToasterModule,
    TranslateModule,
    I2gNgUiModule,
    I2gNgTrackingModule,
  ],
  declarations: [...components, ...directives, ...pipes],
  entryComponents: [MessageToastComponent, ...dialogs],
  exports: [
    ...components,
    ...directives,
    ...pipes,
    TranslateModule,
    I2gNgUiModule,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        UrlService,
        CalculatorService,
        GlobalBannerService,
        DateTimeService,
        CanDeactivateGuard,
        ImageService,
        UtilsService,
        MoneyService,
        LoggerService,
        LoggerStore,
        EmailService,
        LocalDevGuard,
        ImageBuilder,
        ItemSaveService,
        ...pipes,
      ],
    };
  }
}
